/**
 * Feature-local port over Tauri desktop permission IPC.
 *
 * Production code in this feature imports permission checks from here — not
 * from `@/tauri/*` directly.
 */

import { invoke } from '@tauri-apps/api/core';
import { relaunch } from '@tauri-apps/plugin-process';

import {
  checkAccessibilityPermission,
  requestAccessibilityPermission,
} from '@/tauri/dictation/permissions';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { bridgeErrorKind } from '@heidi/plugin-core';

import type { PermissionName, PermissionState } from '../types';

export const isDesktopPermissionGateAvailable = (): boolean => isTauri();

const grantedState = (): PermissionState => 'granted';

export const checkPermission = async (
  permission: PermissionName
): Promise<PermissionState> => {
  if (!isDesktopPermissionGateAvailable()) return grantedState();

  try {
    switch (permission) {
      case 'screen_recording': {
        const granted = await invoke<boolean>('check_screen_recording_permission');
        return granted ? 'granted' : 'denied';
      }
      case 'input_monitoring': {
        const granted = await invoke<boolean>('check_input_monitoring_permission');
        return granted ? 'granted' : 'denied';
      }
      case 'accessibility':
        return checkAccessibilityPermission();
      default: {
        const exhaustive: never = permission;
        return exhaustive;
      }
    }
  } catch (error) {
    logger.error({
      event_type: 'PERMISSION_CHECK_DESKTOP_ERROR',
      details: { permission, errorKind: bridgeErrorKind(error) },
    });
    return 'unknown';
  }
};

export const requestPermission = async (
  permission: PermissionName
): Promise<PermissionState> => {
  if (!isDesktopPermissionGateAvailable()) return grantedState();

  try {
    switch (permission) {
      case 'screen_recording': {
        const granted = await invoke<boolean>('request_screen_recording_permission');
        return granted ? 'granted' : 'denied';
      }
      case 'input_monitoring': {
        const granted = await invoke<boolean>('request_input_monitoring_permission');
        return granted ? 'granted' : 'denied';
      }
      case 'accessibility':
        return requestAccessibilityPermission();
      default: {
        const exhaustive: never = permission;
        return exhaustive;
      }
    }
  } catch (error) {
    logger.error({
      event_type: 'PERMISSION_REQUEST_DESKTOP_ERROR',
      details: { permission, errorKind: bridgeErrorKind(error) },
    });
    return 'unknown';
  }
};

/** Quit & Reopen: a Screen Recording grant reaches capture only in a new process. */
export const relaunchApp = async (): Promise<void> => {
  try {
    await relaunch();
  } catch (error) {
    logger.error({
      event_type: 'PERMISSION_RELAUNCH_DESKTOP_ERROR',
      details: { errorKind: bridgeErrorKind(error) },
    });
  }
};
